import React, { useEffect, useState } from "react";
import api from "../services/api";

const VEHICLE_LABELS = {
  bike: "Bike",
  ape: "3 Wheeler",
  pickup: "Pickup",
  truck: "Truck",
};

const PricingManagement = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingType, setSavingType] = useState(null);
  const [editValues, setEditValues] = useState({});

  const fetchRules = async () => {
    try {
      setLoading(true);
      const res = await api.get("/pricing-rules");
      if (res.data.success) {
        setRules(res.data.rules);

        // Initialize edit state from fetched rules
        const initial = {};
        res.data.rules.forEach((r) => {
          initial[r.vehicleType] = {
            localRadiusKm: r.localRadiusKm,
            localFlatPrice: r.localFlatPrice,
            perKmRate: r.perKmRate,
            rangePercent: r.rangePercent,
            minFare: r.minFare,
            returnTripDiscountPercent: r.returnTripDiscountPercent,
          };
        });
        setEditValues(initial);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load pricing rules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const handleFieldChange = (vehicleType, field, value) => {
    setEditValues((prev) => ({
      ...prev,
      [vehicleType]: {
        ...prev[vehicleType],
        [field]: value,
      },
    }));
  };

  const handleSave = async (vehicleType) => {
    try {
      setSavingType(vehicleType);
      const payload = editValues[vehicleType];

      const res = await api.put(`/pricing-rules/${vehicleType}`, payload);

      if (res.data.success) {
        alert(`${VEHICLE_LABELS[vehicleType] || vehicleType} pricing updated successfully`);
        fetchRules();
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update pricing rule");
    } finally {
      setSavingType(null);
    }
  };

  if (loading && rules.length === 0) {
    return <div style={styles.loading}>Loading Pricing Rules...</div>;
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Pricing Management</h2>
          <p style={styles.subtitle}>
            Set local flat fares, per-km rates, and discount ranges per vehicle
          </p>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.grid}>
        {["bike", "ape", "pickup", "truck"].map((vehicleType) => {
          const values = editValues[vehicleType] || {};

          return (
            <div key={vehicleType} style={styles.card}>
              <h3 style={styles.vehicleTitle}>
                {VEHICLE_LABELS[vehicleType] || vehicleType}
              </h3>

              <div style={styles.formGroup}>
                <label style={styles.label}>Local Radius (km)</label>
                <input
                  type="number"
                  style={styles.input}
                  value={values.localRadiusKm ?? ""}
                  onChange={(e) =>
                    handleFieldChange(vehicleType, "localRadiusKm", Number(e.target.value))
                  }
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Local Flat Price (₹)</label>
                <input
                  type="number"
                  style={styles.input}
                  value={values.localFlatPrice ?? ""}
                  onChange={(e) =>
                    handleFieldChange(vehicleType, "localFlatPrice", Number(e.target.value))
                  }
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Per KM Rate (₹, beyond local)</label>
                <input
                  type="number"
                  style={styles.input}
                  value={values.perKmRate ?? ""}
                  onChange={(e) =>
                    handleFieldChange(vehicleType, "perKmRate", Number(e.target.value))
                  }
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Range (± %)</label>
                <input
                  type="number"
                  style={styles.input}
                  value={values.rangePercent ?? ""}
                  onChange={(e) =>
                    handleFieldChange(vehicleType, "rangePercent", Number(e.target.value))
                  }
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Minimum Fare (₹)</label>
                <input
                  type="number"
                  style={styles.input}
                  value={values.minFare ?? ""}
                  onChange={(e) =>
                    handleFieldChange(vehicleType, "minFare", Number(e.target.value))
                  }
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>One-Way / Return Discount (%)</label>
                <input
                  type="number"
                  style={styles.input}
                  value={values.returnTripDiscountPercent ?? ""}
                  onChange={(e) =>
                    handleFieldChange(vehicleType, "returnTripDiscountPercent", Number(e.target.value))
                  }
                />
              </div>

              <button
                style={styles.saveBtn}
                onClick={() => handleSave(vehicleType)}
                disabled={savingType === vehicleType}
              >
                {savingType === vehicleType ? "Saving..." : "Save Changes"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const styles = {
  page: {
    padding: "30px",
    background: "#f8fafc",
    minHeight: "100vh",
    fontFamily: "'Inter', sans-serif",
  },
  header: {
    marginBottom: "30px",
  },
  title: {
    margin: 0,
    fontSize: "28px",
    color: "#1e293b",
    fontWeight: "700",
  },
  subtitle: {
    margin: "5px 0 0 0",
    color: "#64748b",
    fontSize: "14px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: "20px",
  },
  card: {
    background: "#fff",
    borderRadius: "16px",
    padding: "20px",
    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
  },
  vehicleTitle: {
    margin: "0 0 16px 0",
    fontSize: "18px",
    color: "#1e293b",
  },
  formGroup: {
    marginBottom: "14px",
  },
  label: {
    display: "block",
    fontSize: "11px",
    color: "#94a3b8",
    fontWeight: "700",
    marginBottom: "6px",
    textTransform: "uppercase",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
  },
  saveBtn: {
    width: "100%",
    padding: "12px",
    borderRadius: "8px",
    border: "none",
    background: "#2563eb",
    color: "#fff",
    fontWeight: "600",
    cursor: "pointer",
    marginTop: "6px",
  },
  loading: {
    textAlign: "center",
    padding: "100px",
    fontSize: "20px",
  },
  error: {
    background: "#fef2f2",
    color: "#b91c1c",
    padding: "15px",
    borderRadius: "8px",
    marginBottom: "20px",
  },
};

export default PricingManagement;